import { CreatureAnimation } from "./CreatureAnimation";
import { Background } from "./Background";
import { ProgramType } from "../../../../../data/models";

export const SCENARIO_DEFAULT_WIDTH = 1920;
export const SCENARIO_DEFAULT_RATIO = 1671 / 951;
export const SCENARIO_DEFAULT_HEIGHT =
  SCENARIO_DEFAULT_WIDTH / SCENARIO_DEFAULT_RATIO;

export class Scenario {
  status: string;
  creatureAnimations: Array<CreatureAnimation>;
  focusingIndex?: number | null;
  hoveringIndex?: number | null;
  background: Background;
  context: CanvasRenderingContext2D;
  ratio: number;
  parent: HTMLElement;

  constructor(width: number, height: number) {
    this.status = "play";
    this.creatureAnimations = [];
    this.parent = document.getElementById("canvas-container")!;
    const canvas = document.getElementById("canvas")! as HTMLCanvasElement;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d")!;
    this.context = context;
    this.ratio = width / SCENARIO_DEFAULT_WIDTH;
    this.background = new Background(
      width,
      height,
      context,
      this.creatureAnimations
    );
  }

  destroy() {
    while (this.creatureAnimations.length > 0) {
      const removed = this.creatureAnimations.pop()!;
      removed.destroy();
    }
  }

  updateBackground(index: number) {
    this.background.updateBackground(index);
  }

  updateCreatureAnimations(
    programTypes: ProgramType[],
    creatureTypes: number[],
    creatureIsStops: boolean[],
    isCreating: boolean
  ) {
    const programTypesWithCreating = [...programTypes];
    const creatureTypesWithCreating = [...creatureTypes];
    const creatureIsStopsWithCreating = [...creatureIsStops];
    if (isCreating) {
      programTypesWithCreating.push(ProgramType.None);
      creatureTypesWithCreating.push(creatureTypesWithCreating.length);
      creatureIsStopsWithCreating.push(false);
    }

    let popCount =
      this.creatureAnimations.length - creatureTypesWithCreating.length;
    while (popCount-- > 0) {
      const removed = this.creatureAnimations.pop()!;
      removed.destroy();
    }

    for (let i = 0; i < this.creatureAnimations.length; i++) {
      const programType = this.creatureAnimations[i].programType;
      const creatureType = this.creatureAnimations[i].creatureType;
      const creatureIsCreating = this.creatureAnimations[i].isCreating;
      const creatureIsStop = this.creatureAnimations[i].isStop;
      if (
        creatureType != creatureTypesWithCreating[i] ||
        (creatureIsCreating != isCreating &&
          i == creatureTypesWithCreating.length - 1) ||
        creatureIsStop != creatureIsStopsWithCreating[i] ||
        programType != programTypesWithCreating[i]
      ) {
        this.creatureAnimations[i].update(
          programTypesWithCreating[i],
          creatureTypesWithCreating[i],
          isCreating && i == creatureTypesWithCreating.length - 1,
          creatureIsStopsWithCreating[i]
        );
      }
    }

    for (
      let i = this.creatureAnimations.length;
      i < creatureTypesWithCreating.length;
      i++
    ) {
      const creatureAnimation = new CreatureAnimation(
        i,
        programTypesWithCreating[i],
        creatureTypesWithCreating[i],
        isCreating && i == creatureTypesWithCreating.length - 1,
        creatureIsStopsWithCreating[i],
        this.ratio,
        this.parent
      );
      this.creatureAnimations.push(creatureAnimation);
    }
  }

  setFocus(index: number | null) {
    if (index != null && this.creatureAnimations.length <= index) return;

    if (
      this.focusingIndex != null &&
      this.focusingIndex < this.creatureAnimations.length
    ) {
      this.creatureAnimations[this.focusingIndex].updateFocus(false);
    }
    if (index != null && index < this.creatureAnimations.length) {
      this.creatureAnimations[index].updateFocus(true);
    }
    this.focusingIndex = index;
  }

  getFirstCreatureInRect(cursorLeft: number, cursorTop: number): number | null {
    for (let i = 0; i < this.creatureAnimations.length; i++) {
      const creatureAnimation = this.creatureAnimations[i];
      if (creatureAnimation.inRect(cursorLeft, cursorTop)) {
        return i;
      }
    }
    return null;
  }

  draw() {
    if (this.context) {
      this.background.draw();
    }
  }

  step() {
    for (let i = 0; i < this.creatureAnimations.length; i++) {
      const obj = this.creatureAnimations[i];
      obj.incFrame();
    }
  }
}
